import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { SYSTEM_META } from "@/lib/system";
import { Save, RefreshCw, Send, Plus, Trash2, CheckCircle2, AlertCircle, Upload } from "lucide-react";
import { toast } from "sonner";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm font-mono focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function Settings() {
  const { user } = useAuth();
  const canEdit = ["admin", "supervisor"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  // SLA state (existing)
  const [sla, setSla] = useState(null);
  const [slaMsg, setSlaMsg] = useState("");
  const [slaErr, setSlaErr] = useState("");
  const [slaBusy, setSlaBusy] = useState(false);

  // Telegram state
  const [botInfo, setBotInfo] = useState(null);
  const [whInfo, setWhInfo] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [tgMsg, setTgMsg] = useState("");
  const [tgErr, setTgErr] = useState("");
  const [chats, setChats] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  // Monitoring (auto health-check + Telegram /hc) state
  const [mon, setMon] = useState(null);
  const [monBusy, setMonBusy] = useState(false);
  const [monMsg, setMonMsg] = useState("");
  const [monErr, setMonErr] = useState("");

  useEffect(() => {
    api.get("/settings/sla").then((r) => setSla(r.data));
    if (canEdit) {
      api.get("/telegram/me").then((r) => setBotInfo(r.data)).catch(() => setBotInfo({ ok: false }));
      api.get("/telegram/webhook-info").then((r) => setWhInfo(r.data)).catch(() => setWhInfo({ ok: false }));
      api.get("/telegram/chats").then((r) => setChats(r.data));
      api.get("/merchants").then((r) => setMerchants(r.data));
      api.get("/settings/monitoring").then((r) => setMon(r.data)).catch(() => setMon(null));
    }
  }, [canEdit]);

  const refreshTg = async () => {
    setTgBusy(true);
    try {
      const [me, wh, ch] = await Promise.all([
        api.get("/telegram/me"),
        api.get("/telegram/webhook-info"),
        api.get("/telegram/chats"),
      ]);
      setBotInfo(me.data); setWhInfo(wh.data); setChats(ch.data);
    } finally { setTgBusy(false); }
  };

  const submitSla = async (e) => {
    e.preventDefault(); setSlaBusy(true); setSlaErr(""); setSlaMsg("");
    try {
      const body = {
        response_warning_minutes: Number(sla.response_warning_minutes),
        response_breach_minutes: Number(sla.response_breach_minutes),
        bank_warning_minutes: Number(sla.bank_warning_minutes),
        bank_breach_minutes: Number(sla.bank_breach_minutes),
        final_status_target_minutes: Number(sla.final_status_target_minutes),
      };
      await api.put("/settings/sla", body);
      setSlaMsg("Saved");
      setTimeout(() => setSlaMsg(""), 2000);
    } catch (e2) { setSlaErr(formatErr(e2)); }
    finally { setSlaBusy(false); }
  };

  const submitMon = async (e) => {
    e.preventDefault(); setMonBusy(true); setMonErr(""); setMonMsg("");
    try {
      const body = {
        cs_chat_id: (mon.cs_chat_id || "").trim(),
        cs_thread_id: mon.cs_thread_id === "" || mon.cs_thread_id == null ? null : Number(mon.cs_thread_id),
        enabled_preview: !!mon.enabled_preview,
        enabled_production: !!mon.enabled_production,
        interval_minutes: Math.max(5, Number(mon.interval_minutes) || 15),
        alert_on_failure: !!mon.alert_on_failure,
      };
      const { data } = await api.put("/settings/monitoring", body);
      setMon(data);
      setMonMsg("Saved");
      setTimeout(() => setMonMsg(""), 2000);
    } catch (e2) { setMonErr(formatErr(e2)); }
    finally { setMonBusy(false); }
  };

  const testMonitoring = async () => {
    setMonBusy(true); setMonErr(""); setMonMsg("");
    try {
      await api.post("/settings/monitoring/test");
      setMonMsg("✓ Test message terkirim ke CS-Internal chat");
    } catch (e2) { setMonErr(formatErr(e2)); }
    finally { setMonBusy(false); }
  };

  const setWebhook = async () => {
    setTgBusy(true); setTgErr(""); setTgMsg("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const { data } = await api.post("/telegram/set-webhook", { base_url: base });
      if (data.telegram_response?.ok) {
        setTgMsg(`✓ Webhook registered: ${data.url}`);
      } else {
        setTgErr(data.telegram_response?.description || "Failed");
      }
      await refreshTg();
    } catch (e) { setTgErr(formatErr(e)); }
    finally { setTgBusy(false); }
  };

  const deleteWebhook = async () => {
    if (!confirm("Hapus webhook? Bot tidak akan menerima pesan sampai webhook di-set ulang.")) return;
    setTgBusy(true);
    try { await api.post("/telegram/delete-webhook"); setTgMsg("Webhook removed"); await refreshTg(); }
    catch (e) { setTgErr(formatErr(e)); }
    finally { setTgBusy(false); }
  };

  if (!sla) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="p-6 space-y-4 max-w-5xl animate-slide-up" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Configuration</div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
      </div>

      {/* SLA Settings */}
      <form onSubmit={submitSla} className="space-y-4" data-testid="sla-form">
        <Panel title="Customer Response SLA" testid="sla-response">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Warning Threshold (min)" hint="Timer berubah kuning di atas nilai ini">
              <input data-testid="sla-resp-warn" type="number" min={1} value={sla.response_warning_minutes} onChange={(e) => setSla({ ...sla, response_warning_minutes: e.target.value })} className={ipt} disabled={!canEdit} />
            </Field>
            <Field label="Breach Threshold (min)">
              <input data-testid="sla-resp-breach" type="number" min={1} value={sla.response_breach_minutes} onChange={(e) => setSla({ ...sla, response_breach_minutes: e.target.value })} className={ipt} disabled={!canEdit} />
            </Field>
          </div>
        </Panel>

        <Panel title="Bank / Switcher Response SLA" testid="sla-bank">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Warning Threshold (min)">
              <input data-testid="sla-bank-warn" type="number" min={1} value={sla.bank_warning_minutes} onChange={(e) => setSla({ ...sla, bank_warning_minutes: e.target.value })} className={ipt} disabled={!canEdit} />
            </Field>
            <Field label="Breach Threshold (min)">
              <input data-testid="sla-bank-breach" type="number" min={1} value={sla.bank_breach_minutes} onChange={(e) => setSla({ ...sla, bank_breach_minutes: e.target.value })} className={ipt} disabled={!canEdit} />
            </Field>
            <Field label="Final Status Target (min)" hint="Target ideal untuk avg metric">
              <input data-testid="sla-final-target" type="number" min={1} value={sla.final_status_target_minutes} onChange={(e) => setSla({ ...sla, final_status_target_minutes: e.target.value })} className={ipt} disabled={!canEdit} />
            </Field>
          </div>
        </Panel>

        {slaMsg && <div className="text-xs text-emerald-400" data-testid="sla-msg">{slaMsg}</div>}
        {slaErr && <div className="text-xs text-rose-400" data-testid="sla-err">{slaErr}</div>}
        {canEdit && (
          <button data-testid="sla-save-btn" disabled={slaBusy} className="bg-cyan text-bg font-semibold px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-60">
            <Save className="w-4 h-4" /> Save SLA Settings
          </button>
        )}
      </form>

      {/* Monitoring (Auto Health Check + Telegram /hc) */}
      {canEdit && mon && (
        <form onSubmit={submitMon} className="space-y-3" data-testid="monitoring-form">
          <Panel title="Health Check Monitoring & Telegram /hc" testid="monitoring-panel">
            <p className="text-xs text-slate-400 mb-3 max-w-3xl">
              Auto-monitor menjalankan health check Tarapay & Saturnus setiap N menit dan kirim alert ke chat <b>CS-Internal</b> kalau gagal.
              Tim CS juga bisa trigger manual via <code className="font-mono text-cyan">/hc tarapay</code> atau <code className="font-mono text-cyan">/hc saturnus</code> di chat ini.
              Command lain di chat lain di-ignore (hanya chat yang ter-set di sini yang diterima).
            </p>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <Field label="CS-Internal Chat ID" hint="Group khusus alert (e.g. -1003897859718)">
                <input data-testid="mon-chat-id" value={mon.cs_chat_id || ""} onChange={(e) => setMon({ ...mon, cs_chat_id: e.target.value })} className={`${ipt} font-mono`} placeholder="-100..." />
              </Field>
              <Field label="Topic ID (Optional)" hint="Untuk forum group, isi thread_id topic spesifik. Kosongkan kalau General">
                <input data-testid="mon-thread-id" type="number" value={mon.cs_thread_id ?? ""} onChange={(e) => setMon({ ...mon, cs_thread_id: e.target.value })} className={`${ipt} font-mono`} placeholder="—" />
              </Field>
              <Field label="Interval (menit)" hint="Min 5, default 15">
                <input data-testid="mon-interval" type="number" min={5} max={720} value={mon.interval_minutes ?? 15} onChange={(e) => setMon({ ...mon, interval_minutes: e.target.value })} className={ipt} />
              </Field>
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">
                  Toggle Per-Env (saat ini berjalan di: <b className={mon.current_env === "production" ? "text-emerald-400" : "text-amber-400"}>{(mon.current_env || "preview").toUpperCase()}</b>)
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="mon-prod-wrap">
                  <input type="checkbox" checked={!!mon.enabled_production} onChange={(e) => setMon({ ...mon, enabled_production: e.target.checked })} className="w-4 h-4 accent-cyan" data-testid="mon-enabled-production" />
                  <span>Aktifkan auto-monitor di <b className="text-emerald-400">PRODUCTION</b></span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="mon-prev-wrap">
                  <input type="checkbox" checked={!!mon.enabled_preview} onChange={(e) => setMon({ ...mon, enabled_preview: e.target.checked })} className="w-4 h-4 accent-cyan" data-testid="mon-enabled-preview" />
                  <span>Aktifkan auto-monitor di <b className="text-amber-400">PREVIEW</b> (dev)</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer pt-2 border-t border-line/40" data-testid="mon-alert-wrap">
                  <input type="checkbox" checked={!!mon.alert_on_failure} onChange={(e) => setMon({ ...mon, alert_on_failure: e.target.checked })} className="w-4 h-4 accent-cyan" data-testid="mon-alert" />
                  <span>Kirim alert ke CS-Internal saat health check gagal</span>
                </label>
              </div>
            </div>
            {monMsg && <div className="text-xs text-emerald-400" data-testid="mon-msg">{monMsg}</div>}
            {monErr && <div className="text-xs text-rose-400" data-testid="mon-err">{monErr}</div>}
            <div className="flex gap-2 pt-2 border-t border-line/40">
              <button data-testid="mon-save-btn" disabled={monBusy} className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-60">
                <Save className="w-4 h-4" /> Save Monitoring Config
              </button>
              <button type="button" data-testid="mon-test-btn" onClick={testMonitoring} disabled={monBusy || !mon.cs_chat_id} className="border border-line px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-surface2 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${monBusy ? "animate-spin" : ""}`} /> Test Send to CS-Internal
              </button>
            </div>
          </Panel>
        </form>
      )}

      {/* Telegram */}
      {canEdit && (
        <>
          <Panel
            title="Telegram Bot"
            testid="telegram-panel"
            action={
              <button data-testid="tg-refresh-btn" onClick={refreshTg} disabled={tgBusy} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${tgBusy ? "animate-spin" : ""}`} /> Refresh
              </button>
            }
          >
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="border border-line rounded-sm p-3" data-testid="tg-bot-status">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Bot Connection</div>
                {botInfo?.ok ? (
                  <div>
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm font-semibold">{botInfo.result.first_name}</span></div>
                    <div className="text-[10px] font-mono text-slate-500 mt-1">@{botInfo.result.username} · id:{botInfo.result.id}</div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-rose-400 text-xs"><AlertCircle className="w-4 h-4" /> {botInfo?.description || "Not configured"}</div>
                )}
              </div>
              <div className="border border-line rounded-sm p-3" data-testid="tg-webhook-status">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Webhook</div>
                {whInfo?.result?.url ? (
                  <div>
                    <div className="text-xs text-emerald-400 font-mono break-all">{whInfo.result.url}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Pending: {whInfo.result.pending_update_count ?? 0} · IP: {whInfo.result.ip_address || "—"}</div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-400">Not registered yet</div>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <button data-testid="tg-set-webhook-btn" onClick={setWebhook} disabled={tgBusy} className="bg-cyan text-bg font-semibold px-4 py-1.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5 hover:bg-cyan-dark disabled:opacity-60">
                  <Send className="w-3 h-3" /> Set Webhook
                </button>
                {whInfo?.result?.url && (
                  <button data-testid="tg-delete-webhook-btn" onClick={deleteWebhook} disabled={tgBusy} className="border border-rose-500/30 text-rose-400 px-4 py-1.5 rounded-sm text-xs uppercase tracking-wider hover:bg-rose-500/10 disabled:opacity-60">
                    Remove Webhook
                  </button>
                )}
              </div>
            )}
            {tgMsg && <div className="text-xs text-emerald-400 mt-2" data-testid="tg-msg">{tgMsg}</div>}
            {tgErr && <div className="text-xs text-rose-400 mt-2" data-testid="tg-err">{tgErr}</div>}
            <div className="mt-3 border-t border-line pt-3 text-[10px] text-slate-500">
              <div className="uppercase tracking-wider mb-1">Cara Setup</div>
              1. Tambahkan bot <span className="text-cyan font-mono">@{botInfo?.result?.username || "ST_TARA_SUPPORT_BOT"}</span> ke group customer di Telegram.
              <br/>2. Beri bot akses baca message (di group settings: ubah Group privacy → Bot dapat baca semua pesan, atau jadikan admin).
              <br/>3. Kirim 1 pesan test di group — sistem otomatis buat tiket. Lihat tabel &quot;Chat Mapping&quot; di bawah → klik Edit untuk attach merchant.
            </div>
          </Panel>

          <Panel
            title={`Chat Mapping (${chats.length})`}
            testid="tg-chats-panel"
            action={
              <div className="flex items-center gap-1.5">
                <label data-testid="tg-bulk-import-btn" className="text-[10px] uppercase tracking-wider border border-line text-slate-300 px-2 py-1 rounded-sm hover:bg-surface2 cursor-pointer flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Import Excel
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      fd.append("dry_run", "false");
                      try {
                        const { data } = await api.post("/telegram/chats/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        toast.success(`Imported · created ${data.created}, updated ${data.updated}, skipped Gabung CPG ${data.skipped_gabung_cpg}`);
                        refreshTg();
                      } catch (e2) {
                        toast.error(formatErr(e2));
                      } finally {
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
                <button data-testid="tg-new-chat-btn" onClick={() => { setEditing({ chat_id: "", chat_title: "", thread_id: null, thread_name: "", merchant_name: "", system: "TARAPAY", customer_type: "FOREX", default_service_type: "PAYIN", default_language: "ID", enable_topic_autodiscovery: false }); setCreating(true); }} className="text-[10px] uppercase tracking-wider border border-cyan/40 text-cyan px-2 py-1 rounded-sm hover:bg-cyan/10 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Map chat
                </button>
              </div>
            }
          >
            {chats.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-6" data-testid="empty-chats">
                Belum ada mapping. Pesan dari chat yang tidak dipetakan tetap masuk sebagai tiket dengan merchant = nama group.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="tg-chats-table">
                <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-line">
                  <tr>
                    <th className="text-left px-2 py-2">Chat ID</th>
                    <th className="text-left px-2 py-2">Topic</th>
                    <th className="text-left px-2 py-2">Group Title</th>
                    <th className="text-left px-2 py-2">→ Merchant</th>
                    <th className="text-left px-2 py-2">System</th>
                    <th className="text-left px-2 py-2">Defaults</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {chats.map((c) => (
                    <tr key={c.id} className="border-b border-line/40 hover:bg-surface2" data-testid={`tg-chat-row-${c.id}`}>
                      <td className="px-2 py-2 font-mono">{c.chat_id}</td>
                      <td className="px-2 py-2 text-[10px]">
                        {c.thread_id ? (
                          <span className="font-mono text-cyan">#{c.thread_id}{c.thread_name ? ` · ${c.thread_name}` : ""}</span>
                        ) : (
                          <span className="text-slate-600">— (root)</span>
                        )}
                      </td>
                      <td className="px-2 py-2">{c.chat_title || <span className="text-slate-600">—</span>}</td>
                      <td className="px-2 py-2 font-semibold text-cyan">{c.merchant_name}</td>
                      <td className="px-2 py-2"><Badge color={SYSTEM_META[c.system]?.color}>{SYSTEM_META[c.system]?.short}</Badge></td>
                      <td className="px-2 py-2 text-[10px] text-slate-400">{c.default_service_type} · {c.customer_type} · {c.default_language}{c.auto_discovered ? " · auto" : ""}</td>
                      <td className="px-2 py-2 text-right">
                        <button data-testid={`tg-edit-${c.id}`} onClick={() => { setEditing(c); setCreating(false); }} className="text-xs text-cyan hover:underline mr-2">Edit</button>
                        <button data-testid={`tg-delete-${c.id}`} onClick={async () => { if (confirm("Hapus mapping?")) { await api.delete(`/telegram/chats/${c.id}`); refreshTg(); } }} className="text-xs text-rose-400 hover:underline"><Trash2 className="w-3 h-3 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          {editing && (
            <ChatModal
              chat={editing}
              creating={creating}
              merchants={merchants}
              onClose={() => { setEditing(null); setCreating(false); }}
              onSaved={() => { setEditing(null); setCreating(false); refreshTg(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

function ChatModal({ chat, creating, merchants, onClose, onSaved }) {
  const [f, setF] = useState(chat);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (creating) await api.post("/telegram/chats", f);
      else {
        const { chat_id, thread_id, id, created_at, auto_discovered, ...body } = f;
        await api.patch(`/telegram/chats/${chat.id}`, body);
      }
      onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tg-chat-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-md space-y-3">
        <h3 className="text-lg font-bold">{creating ? "Map Telegram Chat / Topic" : "Edit Chat Mapping"}</h3>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Chat ID</label>
          <input data-testid="tgm-chat-id" required disabled={!creating} value={f.chat_id} onChange={(e) => setF({ ...f, chat_id: e.target.value })} className={`${ipt} font-mono`} placeholder="-1001234567890" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Topic ID</label>
            <input
              data-testid="tgm-thread-id"
              type="number"
              disabled={!creating}
              value={f.thread_id ?? ""}
              onChange={(e) => setF({ ...f, thread_id: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className={`${ipt} font-mono`}
              placeholder="kosong = group root"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Topic Name (opsional)</label>
            <input data-testid="tgm-thread-name" value={f.thread_name || ""} onChange={(e) => setF({ ...f, thread_name: e.target.value })} className={ipt} placeholder="contoh: CPG · PAYIN" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Group Title (opsional)</label>
          <input data-testid="tgm-title" value={f.chat_title || ""} onChange={(e) => setF({ ...f, chat_title: e.target.value })} className={ipt} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Merchant</label>
          <input
            data-testid="tgm-merchant"
            list="merchants-tg-list"
            required
            value={f.merchant_name}
            onChange={(e) => setF({ ...f, merchant_name: e.target.value })}
            className={ipt}
          />
          <datalist id="merchants-tg-list">
            {merchants.map((m) => <option key={m.id} value={m.name} />)}
          </datalist>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">System</label>
            <select data-testid="tgm-system" value={f.system} onChange={(e) => setF({ ...f, system: e.target.value })} className={ipt}>
              <option value="TARAPAY">Tarapay</option>
              <option value="SATURNUS">Saturnuspay</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Customer Type</label>
            <select data-testid="tgm-customer" value={f.customer_type} onChange={(e) => setF({ ...f, customer_type: e.target.value })} className={ipt}>
              <option value="FOREX">Forex</option>
              <option value="GAMING">Gaming</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Default Service</label>
            <select data-testid="tgm-service" value={f.default_service_type} onChange={(e) => setF({ ...f, default_service_type: e.target.value })} className={ipt}>
              <option value="PAYIN">Pay-In</option>
              <option value="PAYOUT">Payout</option>
              <option value="WITHDRAWAL">Withdrawal</option>
              <option value="TOPUP">Top-Up</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Default Language</label>
            <select data-testid="tgm-lang" value={f.default_language} onChange={(e) => setF({ ...f, default_language: e.target.value })} className={ipt}>
              <option value="ID">Indonesia</option>
              <option value="ZH">中文</option>
              <option value="EN">English</option>
            </select>
          </div>
        </div>
        {/* Auto-discovery toggle — only meaningful on chat-level mappings (no thread_id) */}
        {(!f.thread_id) && (
          <div className="border border-line rounded-sm p-3 bg-surface2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="tgm-autodiscover"
                checked={!!f.enable_topic_autodiscovery}
                onChange={(e) => setF({ ...f, enable_topic_autodiscovery: e.target.checked })}
                className="accent-cyan mt-0.5"
              />
              <div>
                <div className="text-xs font-medium">Enable topic auto-discovery</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Kalau aktif: setiap topic baru di grup ini yang belum di-register akan otomatis dibuatkan mapping (inherit merchant + system dari chat ini). <span className="text-amber-400">Default OFF</span> untuk mencegah &quot;topic terus ke-generate&quot;. Aktifkan hanya kalau memang mau setiap topic diperlakukan sebagai merchant yang sama.
                </div>
              </div>
            </label>
          </div>
        )}
        {err && <div className="text-xs text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Cancel</button>
          <button data-testid="tgm-save" disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}
